import { api, ApiError, type ExportRecord } from '../api/client.ts';

/**
 * Waiting for a cut and handing it over.
 *
 * The producer pressed one button; making them find the file on another screen
 * afterwards is a second job. So the request is followed until the worker has
 * the file, then the browser is handed it.
 */

/** Copy-mode cuts finish in well under a second; a re-encode takes a few. */
const POLL_MS = 700;
const GIVE_UP_MS = 120_000;

export type ExportOutcome =
  | { kind: 'ready'; record: ExportRecord }
  | { kind: 'failed'; message: string | null }
  | { kind: 'timeout' };

export async function waitForExport(
  id: number,
  isCancelled: () => boolean,
): Promise<ExportOutcome> {
  const deadline = Date.now() + GIVE_UP_MS;

  while (Date.now() < deadline) {
    if (isCancelled()) return { kind: 'timeout' };

    let record: ExportRecord;
    try {
      record = await api.export(id);
    } catch (err) {
      // A blip while polling is not a failed export; keep asking.
      if (err instanceof ApiError && err.status >= 500) {
        await sleep(POLL_MS);
        continue;
      }
      throw err;
    }

    if (record.status === 'ready') return { kind: 'ready', record };
    if (record.status === 'failed') {
      return { kind: 'failed', message: record.error_message };
    }
    await sleep(POLL_MS);
  }
  return { kind: 'timeout' };
}

/**
 * Starts the download. A real anchor click rather than assigning `location`,
 * so the server's Content-Disposition decides the filename — which is how the
 * producer's Arabic label survives (§9.5).
 */
export function startDownload(url: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = '';
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
